import { useCallback } from 'react'

import { LogoDigitizationSetting, LogoStatus } from '../../__generated__/graphql/catalog/graphql'

import { DtcLogoSetupFormValues } from './types'

type UploadLogoResponse = {
  digitizationSetting: LogoDigitizationSetting
  id: string
  name: string
  notes: string
  preview: string
  filename: string | null
  priorityProcessing: boolean
  shopifyCustomerId: string
  status: LogoStatus
}

type UploadLogoInput = Pick<
  Required<DtcLogoSetupFormValues>,
  'file' | 'name' | 'digitization_setting' | 'notes'
>

/** A logo upload the API answered with an error status. */
class LogoUploadError extends Error {
  status: number
  /** The file's MIME type and size, so a rejected file can be told apart from a server fault. */
  fileType: string
  fileSizeKb: string

  constructor(status: number, file: Blob | undefined) {
    // Not the statusText, which is empty over HTTP/2
    super(`Logo upload failed (${status})`)
    this.name = 'LogoUploadError'
    this.status = status
    this.fileType = file?.type || 'unknown'
    this.fileSizeKb = file ? String(Math.round(file.size / 1024)) : 'unknown'
  }
}

/** RUM context for a failed logo upload, with the status and file when the API answered. */
export function logoUploadErrorContext(error: unknown): Record<string, string> {
  return {
    label: 'embroidery-logo-upload-failed',
    ...(error instanceof LogoUploadError && {
      httpStatus: String(error.status),
      fileType: error.fileType,
      fileSizeKb: error.fileSizeKb,
    }),
  }
}

/**
 * Upload a new DTC embroidery logo.
 */
export default function useEmbroideryLogoUpload() {
  const uploadLogo = useCallback(async (values: UploadLogoInput) => {
    const formData = new FormData()
    formData.set('file', values.file ?? '')
    formData.set('name', values.name ?? '')
    formData.set('digitization_setting', values.digitization_setting ?? '')
    formData.set('notes', values.notes ?? '')

    const url = `/api/embroidery/v1/logos`

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    })
    // Checked before parsing, since an error body may not be JSON
    if (!response.ok) throw new LogoUploadError(response.status, values.file)

    const data: UploadLogoResponse = await response.json()
    return {
      ...data,
      digitizationSetting: data.digitizationSetting,
      // A net-new logo has not been digitized yet, so it never requires
      // manual handling
      manualHandling: false,
    }
  }, [])

  return uploadLogo
}
